import type { PaymentMethod, Stripe, StripeElements } from '@stripe/stripe-js'
import { getSavedCardPaymentMethodId, isStripePaymentCardV2, type SavedStripeCard } from './paymentUtils'

export interface PaymentCardIdentity {
  paymentMethodId?: string
  fingerprint?: string
  brand?: string
  last4?: string
  funding?: string
  expMonth?: number
  expYear?: number
  walletType?: string
  country?: string
}

interface PreparePaymentCardIdentityInput {
  account: Pick<App.AuthAccount, 'email' | 'fullName'>
  elements: StripeElements
  stripe: Stripe
}

export function savedStripeCardToPaymentCardIdentity(card: SavedStripeCard): PaymentCardIdentity {
  if (isStripePaymentCardV2(card)) {
    return {
      paymentMethodId: card.paymentMethodId,
      fingerprint: card.fingerprint,
      brand: card.brand,
      last4: card.last4,
      funding: card.funding,
      expMonth: card.expMonth,
      expYear: card.expYear,
      country: card.country,
    }
  }

  return {
    paymentMethodId: getSavedCardPaymentMethodId(card),
    fingerprint: card.fingerprint,
    brand: card.brand,
    last4: card.last4,
    expMonth: card.exp_month,
    expYear: card.exp_year,
    country: card.country,
  }
}

export function stripePaymentMethodToPaymentCardIdentity(paymentMethod: PaymentMethod): PaymentCardIdentity {
  if (paymentMethod.type !== 'card') return {}

  return {
    paymentMethodId: paymentMethod.id,
    fingerprint: paymentMethod.card?.fingerprint ?? undefined,
    brand: paymentMethod.card?.brand ?? undefined,
    last4: paymentMethod.card?.last4 ?? undefined,
    funding: paymentMethod.card?.funding ?? undefined,
    expMonth: paymentMethod.card?.exp_month ?? undefined,
    expYear: paymentMethod.card?.exp_year ?? undefined,
    walletType: paymentMethod.card?.wallet?.type ?? undefined,
    country: paymentMethod.card?.country ?? undefined,
  }
}

export async function preparePaymentCardIdentity(input: PreparePaymentCardIdentityInput): Promise<PaymentCardIdentity> {
  const { account, elements, stripe } = input
  const { error: submitError } = await elements.submit()

  if (submitError) {
    throw new Error(submitError.message)
  }

  const { error, paymentMethod } = await stripe.createPaymentMethod({
    elements,
    params: {
      billing_details: {
        name: account.fullName,
        email: account.email,
      },
    },
  })

  if (error) {
    throw new Error(error.message)
  }

  return stripePaymentMethodToPaymentCardIdentity(paymentMethod)
}

export function hasCompletePaymentCardDetails(card: PaymentCardIdentity) {
  return Boolean(
    card.brand &&
    card.last4 &&
    card.funding &&
    card.expMonth &&
    card.expYear,
  )
}

export function arePaymentCardsLikelyTheSame(
  card: PaymentCardIdentity,
  other?: PaymentCardIdentity,
) {
  if (!other) return false

  return Boolean(
    (card.paymentMethodId && other.paymentMethodId && card.paymentMethodId === other.paymentMethodId) ||
    hasMatchingCardFingerprintAndExpiry(card, other) ||
    (!card.fingerprint && hasMatchingCardDetails(card, other)),
  )
}

export function findDuplicatePaymentCardKeys(
  cardsByKey: Record<string, PaymentCardIdentity | undefined>,
): Set<string> {
  const keys = Object.keys(cardsByKey)
  const duplicates = new Set<string>()

  for (let i = 0; i < keys.length; i++) {
    for (let j = i + 1; j < keys.length; j++) {
      const left = cardsByKey[keys[i]]
      const right = cardsByKey[keys[j]]
      if (left && right && (arePaymentCardsLikelyTheSame(left, right) || arePaymentCardsLikelyTheSame(right, left))) {
        duplicates.add(keys[i])
        duplicates.add(keys[j])
      }
    }
  }

  return duplicates
}

function hasMatchingCardFingerprintAndExpiry(
  card: PaymentCardIdentity,
  other: PaymentCardIdentity,
) {
  return Boolean(
    card.fingerprint &&
    other.fingerprint &&
    card.expMonth &&
    card.expYear &&
    other.expMonth &&
    other.expYear &&
    card.fingerprint === other.fingerprint &&
    card.expMonth === other.expMonth &&
    card.expYear === other.expYear,
  )
}

function hasMatchingCardDetails(
  card: PaymentCardIdentity,
  other: PaymentCardIdentity,
) {
  return Boolean(
    hasCompletePaymentCardDetails(card) &&
    hasCompletePaymentCardDetails(other) &&
    card.brand === other.brand &&
    card.last4 === other.last4 &&
    card.funding === other.funding &&
    card.expMonth === other.expMonth &&
    card.expYear === other.expYear,
  )
}
